import { Bot, SquareTerminal } from '@orgus/ui/icons';
import { useEffect, useMemo, useState } from 'react';
import type { Checkout, TermOpenMode } from '@orgus/protocol';
import { Button, Dialog, ErrorState, Segmented, Select, Textarea, shortPath } from '@orgus/ui';
import { toUiError } from '../../lib/api.ts';
import type { UiError } from '../../lib/api.ts';
import { useOrgus, useMachines, useProjects } from '../../lib/store.ts';
import { MODEL_ALIASES, modelOptionLabel } from '../sessions/labels.ts';
import { accountOptionsOf, pickedAccountId, useLaunchAccountChoices } from '../launch-accounts/data.ts';
import { openTerminal } from './api.ts';

/**
 * Claude Code's aliases: each resolves to a model the account can use. A pinned id listed here
 * went stale (claude-haiku-5 never existed, ADR 0059); the agent's preflight refuses one that does not exist.
 */
const MODELS = Object.keys(MODEL_ALIASES);

export interface NewTerminalPrefill {
  machineId?: string;
  checkoutId?: string;
  mode?: Exclude<TermOpenMode, 'attach' | 'sign-in'>;
  prompt?: string;
}

function checkoutLabel(c: Checkout, project: string): string {
  return `${project}: ${c.branch ?? 'detached'} in ${shortPath(c.path, 40)}${c.kind === 'worktree' ? ' (worktree)' : ''}`;
}

/**
 * Start Claude Code in a terminal Orgus owns (so anyone allowed can take it over), or open
 * a shell on a checkout (admins only). Opens the new terminal when it starts.
 */
export function NewTerminalDialog({
  open,
  onOpenChange,
  prefill,
  onOpened,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  prefill?: NewTerminalPrefill;
  onOpened: (termId: string) => void;
}) {
  const machines = useMachines();
  const projects = useProjects();
  const checkouts = useOrgus((s) => s.checkouts);
  const [mode, setMode] = useState<'launch' | 'shell'>('launch');
  const [machineId, setMachineId] = useState('');
  const [checkoutId, setCheckoutId] = useState('');
  const [prompt, setPrompt] = useState('');
  const [model, setModel] = useState('');
  // The launch account (ADR 0071): '' leaves it to the project's default, else the machine's own sign-in.
  const [launchAccountId, setLaunchAccountId] = useState('');
  const accountChoices = useLaunchAccountChoices();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<UiError | null>(null);
  const [tried, setTried] = useState(false);

  useEffect(() => {
    if (!open) return;
    const online = machines.find((m) => m.online);
    setMode(prefill?.mode ?? 'launch');
    setMachineId(prefill?.machineId ?? online?.id ?? '');
    setCheckoutId(prefill?.checkoutId ?? '');
    setPrompt(prefill?.prompt ?? '');
    setModel('');
    setLaunchAccountId('');
    setError(null);
    setTried(false);
    // Reset only when the dialog opens.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const projectName = useMemo(() => new Map(projects.map((p) => [p.id, p.name])), [projects]);
  const machineCheckouts = useMemo(
    () =>
      Object.values(checkouts)
        .filter((c) => c.machineId === machineId)
        .sort((a, b) => (projectName.get(a.projectId) ?? '').localeCompare(projectName.get(b.projectId) ?? '') || Number(a.kind === 'worktree') - Number(b.kind === 'worktree')),
    [checkouts, machineId, projectName],
  );
  useEffect(() => {
    if (checkoutId && !machineCheckouts.some((c) => c.id === checkoutId)) setCheckoutId('');
  }, [machineCheckouts, checkoutId]);

  // Offered for Claude Code once there are two accounts to choose between, as in New session.
  const projectId = (checkoutId && checkouts[checkoutId]?.projectId) || '';
  const accountOptions = accountChoices ? accountOptionsOf(accountChoices, projectId) : [];
  const chosenAccount = mode === 'launch' ? pickedAccountId(accountChoices, launchAccountId) : null;

  const machine = machines.find((m) => m.id === machineId);
  const machineError = !machineId ? 'Choose a machine.' : machine && !machine.online ? `${machine.name} is offline.` : null;
  const checkoutError = !checkoutId ? 'Choose where it runs.' : null;
  const promptError = mode === 'launch' && !prompt.trim() ? 'Tell Claude what to do.' : null;
  const invalid = !!(machineError || checkoutError || promptError);

  const submit = async () => {
    setTried(true);
    if (invalid) return;
    setBusy(true);
    setError(null);
    try {
      const r = await openTerminal({
        machineId,
        mode,
        checkoutId,
        cols: 120,
        rows: 32,
        ...(mode === 'launch' ? { prompt: prompt.trim(), ...(model ? { model } : {}), ...(chosenAccount ? { launchAccountId: chosenAccount } : {}) } : {}),
      });
      onOpenChange(false);
      onOpened(r.term.id);
    } catch (e) {
      setError(toUiError(e, { what: 'The terminal did not start.', next: 'Check the details, then try again.' }));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      size="lg"
      title="New terminal"
      description={
        mode === 'launch'
          ? 'Claude Code runs in a terminal Orgus owns, so you can watch it live and take the wheel.'
          : 'A shell on the machine, in the checkout you pick. Admins only; every keystroke is recorded.'
      }
      footer={
        <>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button variant="primary" loading={busy} onClick={() => void submit()} disabled={tried && invalid}>
            {mode === 'launch' ? 'Start session' : 'Open shell'}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <Segmented
          label="What runs in the terminal"
          size="md"
          value={mode}
          onChange={setMode}
          options={[
            { value: 'launch', label: 'Claude Code', icon: <Bot /> },
            { value: 'shell', label: 'Shell', icon: <SquareTerminal /> },
          ]}
        />
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <Select
            label="Machine"
            value={machineId}
            placeholder={machines.length ? 'Choose a machine' : 'No machines yet'}
            options={machines.map((m) => ({ value: m.id, label: m.online ? m.name : `${m.name} (offline)`, disabled: !m.online }))}
            onValueChange={setMachineId}
            error={tried ? machineError : null}
          />
          <Select
            label="Where"
            value={checkoutId}
            placeholder={machineCheckouts.length ? 'Choose a checkout' : 'No checkouts on this machine'}
            options={machineCheckouts.map((c) => ({ value: c.id, label: checkoutLabel(c, projectName.get(c.projectId) ?? 'Project') }))}
            onValueChange={setCheckoutId}
            error={tried ? checkoutError : null}
          />
        </div>
        {mode === 'launch' && (
          <>
            <Textarea
              label="Prompt"
              placeholder="What should Claude do"
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              rows={4}
              error={tried ? promptError : null}
            />
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <Select
                label="Model"
                value={model}
                options={[{ value: '', label: 'Default' }, ...MODELS.map((m) => ({ value: m, label: modelOptionLabel(m) }))]}
                onValueChange={setModel}
              />
              {accountChoices && <Select label="Account" options={accountOptions} value={launchAccountId} onValueChange={setLaunchAccountId} />}
            </div>
          </>
        )}
        {error && <ErrorState error={error} variant="inline" />}
      </div>
    </Dialog>
  );
}
